import React, { useEffect, useMemo } from 'react'
import { View, StyleSheet, ScrollView, TouchableOpacity, Platform, Dimensions, Image } from 'react-native'
import { Icon, Text, useTheme } from '@ui-kitten/components'
import HeaderIconButton from '../components/HeaderIconButton'
import { HintView } from '../components/HintView'
import { useTranslation } from 'react-i18next'
import { userAtom, userStoreIdAtom, dailyStatsSalesAtom, organizationAtom, organizationSubscriptionInfoAtom, productsDictAtom, storesDictAtom, storesListAtom, userInDifferentTimeZoneAtom, userRoleAtom, userStoreAtom, userStoreDailyStatAtom, currencyFormatterAtom, productsUpdatedAtom } from '../global/AtomsProvider'
import { getLocalStoreDate } from '../utils/timezoneFunctions'
import { format } from 'date-fns'
import { atom, useAtom, useAtomValue } from 'jotai'
import { getProductsList } from '../utils/productListHelpers'
import { loadable } from "jotai/utils"
import { useToast } from "react-native-toast-notifications"
import { countClientOrdersFromServer } from '../global/Firebase/firebase'
import { showsOnTopFor } from '../utils/showOnTop'

const incompleteChecklistItemsCountAtom = atom((get) => { return get(userStoreDailyStatAtom)?.incompleteChecklistItems })
const stockAlertsCountAtom = atom((get) => {
  let productsDict = get(productsDictAtom)
  let userStoreId = get(userStoreIdAtom)
  let activeProductsList = getProductsList(productsDict, true, userStoreId)
  let count = 0
  activeProductsList?.forEach(product => {
    if ((product.storeStock?.[userStoreId] ?? 0) <= (product.stockThreshold || 1) && product.importance > 1) {
      count++
    }
  })
  return count
})
const activeClientOrdersCountAtom = loadable(atom(async (get) => {
  let orgId = get(organizationAtom)?.id
  return await countClientOrdersFromServer(orgId)
}))

const menuItems = [
  {
    title: 'newSale.newSale',
    onPress: (navigation) => navigation.navigate({name: 'NewSale', key: 'NewSale'}),
    icon: 'plus-outline',
    permission: 'NewSale',
    module: 'sales',
    testID: 'menu-new-sale'
  },
  {
    title: 'salesHistory.salesHistory',
    onPress: (navigation) => navigation.navigate('SalesHistory'),
    icon: 'clock-outline',
    permission: 'SalesHistory',
    module: 'sales',
    testID: 'menu-sales-history'
  },
  {
    title: 'complexSales.complexSales',
    onPress: (navigation) => navigation.navigate('ComplexSales'),
    icon: 'file-text-outline',
    permission: 'ComplexSales',
    module: 'complexSales',
    testID: 'menu-complex-sales'
  },
  {
    title: 'supplies.supplies',
    onPress: (navigation) => navigation.navigate('Supplies'),
    icon: 'car-outline',
    permission: 'Supplies',
    module: 'supplies',
    testID: 'menu-supplies'
  },
  {
    title: 'writeOffs.writeOffs',
    onPress: (navigation) => navigation.navigate('WriteOffs'),
    icon: 'trash-2-outline',
    permission: 'WriteOffs',
    module: 'writeOffs',
    testID: 'menu-write-offs'
  },
  {
    title: 'shift.shift',
    onPress: (navigation) => navigation.navigate('Shift'),
    icon: 'hash-outline',
    permission: 'Shift',
    module: 'shift',
    testID: 'menu-shift'
  },
  {
    title: 'checklist.checklist',
    onPress: (navigation) => navigation.navigate('SellerChecklist'),
    icon: 'checkmark-circle-outline',
    alert: incompleteChecklistItemsCountAtom,
    permission: 'Checklist',
    module: 'checklist',
    testID: 'menu-checklist'
  },
  {
    title: 'clientOrders.clientOrders',
    onPress: (navigation) => navigation.navigate('ClientOrders'),
    icon: 'bell-outline',
    alert: activeClientOrdersCountAtom,
    permission: 'ClientOrders',
    module: 'ecommerce',
    testID: 'menu-client-orders'
  },
  {
    title: 'updates.updates',
    onPress: (navigation) => navigation.navigate('Updates'),
    icon: 'activity-outline',
    permission: 'Updates',
    module: 'updates',
    testID: 'menu-updates'
  },
  {
    title: 'productStocks.productStocks',
    onPress: (navigation) => navigation.navigate('ProductStocks'),
    icon: 'clipboard-outline',
    permission: 'ProductStocks',
    alert: stockAlertsCountAtom,
    module: 'stock',
    testID: 'menu-product-stocks'
  },
  {
    title: 'productsAndIngredients.productsAndIngredients',
    onPress: (navigation) => navigation.navigate('Products'),
    icon: 'pricetags-outline',
    permission: 'Products',
    testID: 'menu-products'
  },
  {
    title: 'payments.payments',
    onPress: (navigation) => navigation.navigate('PaymentTransactions'),
    icon: 'credit-card-outline',
    permission: 'Payments',
    module: 'payments',
    testID: 'menu-payments'
  },
  {
    title: 'printStickers.printStickers',
    onPress: (navigation) => navigation.navigate('PrintStickers', { products: null }),
    icon: 'printer-outline',
    permission: 'PrintStickers',
    module: 'printStickers',
    testID: 'menu-print-stickers'
  },
  {
    title: 'manufactures.manufactures',
    onPress: (navigation) => navigation.navigate('Manufactures'),
    icon: 'pantone-outline',
    permission: 'Manufactures',
    module: 'manufactures',
    testID: 'menu-manufactures'
  },
  {
    title: 'spendings.spendings',
    onPress: (navigation) => navigation.navigate('Spendings'),
    icon: 'shopping-cart-outline',
    permission: 'Spendings',
    module: 'spendings',
    testID: 'menu-spendings'
  },
  {
    title: 'stats.stats',
    onPress: (navigation) => navigation.navigate('Stats'),
    icon: 'bar-chart-2-outline',
    permission: 'Stats',
    testID: 'menu-stats'
  },
  {
    title: 'clients.clients',
    onPress: (navigation) => navigation.navigate('Clients'),
    icon: 'people-outline',
    roles: ['admin'],
    permission: 'Clients',
    module: 'clients',
    testID: 'menu-clients'
  },
  {
    title: 'revisions.revisions',
    onPress: (navigation) => navigation.navigate('Revisions'),
    icon: 'edit-2-outline',
    permission: 'Revisions',
    module: 'revisions',
    testID: 'menu-revisions'
  },
  {
    title: 'transfers.transfers',
    onPress: (navigation) => navigation.navigate('Transfers'),
    icon: 'swap-outline',
    permission: 'Transfers',
    module: 'transfers',
    testID: 'menu-transfers'
  },
  {
    title: 'salary.salary',
    onPress: (navigation) => navigation.navigate('Salary', {preselectedUser: null}),
    icon: 'person-outline',
    permission: 'Salary',
    module: 'salary',
    testID: 'menu-salary'
  },
  {
    title: 'settings.settings',
    onPress: (navigation) => navigation.navigate('Settings'),
    icon: 'settings-2-outline',
    permission: 'Settings',
    testID: 'menu-settings'
  },
]

export default function HomeScreen({ navigation }) {  
  const userStore = useAtomValue(userStoreAtom)
  const storesList = useAtomValue(storesListAtom)
  const organization = useAtomValue(organizationAtom)
  const user = useAtomValue(userAtom)
  const userRole = useAtomValue(userRoleAtom)

  // useEffect(() => {
  //   if (organization?.name != null && organization.pricingModel === 'paywall_2024_07_18' && organization.subscriptionPlan == null && Platform.OS !== 'android') {
  //     setTimeout(() => {
  //       navigation.navigate('Paywall', { showCloseButton: false })
  //     }, 0)
  //   }
  // }, [organization])

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          text={user?.name}
          onPress={() => {
            navigation.navigate('Profile')
          }}
        />
      ),
      headerLeft: storesList?.length > 1 ? () => (
      <Text 
        style={styles.leftHeader}
        >{userStore?.name}</Text>
      ) : undefined,
      headerTitleAlign: 'center'
    })
  }, [userStore?.name, storesList?.length])

  return (
    <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' contentContainerStyle={{ flexGrow: 1 }}>
      <BillingAlertView navigation={navigation} />
      {/* <UpgradePromptView navigation={navigation} /> */}
      <TimeZoneAlertView navigation={navigation} />
      <Text category='h4' style={styles.title}>
        {organization?.name}
      </Text>
      { (userRole?.permissions?.['mainScreenStats'] || userRole?.id === 'admin') && <HomeScreenStats />}
      <View style={styles.menuContainer}>
        {
          menuItems.map((item, _index) => (
            <HomeScreenCard key={item.title} item={item} navigation={navigation} />
          ))
        }
      </View>
      <HomeBottomView />
    </ScrollView>
  )
}

const TimeZoneAlertView = React.memo(function TimeZoneAlertView({ navigation }) {
  const { t } = useTranslation()
  const userInDifferentTimeZone = useAtomValue(userInDifferentTimeZoneAtom)
  const store = useAtomValue(userStoreAtom)
  const userRole = useAtomValue(userRoleAtom)
  const toast = useToast()
  if (userInDifferentTimeZone) {
    return (
      <HintView
        iconName='info-outline'
        style={styles.alertHintView}
        text={t('differentTimeZoneText', {time: format(getLocalStoreDate(), 'd MMM HH:mm')})} 
        action={() => { 
          if (userRole?.permissions?.['editSettings'] || userRole?.id === 'admin') {
            navigation.navigate('StoreDetail', {detail: store})
          } else {
            toast.show(t('permissionDenied'), {
              type: "danger",
              placement: "bottom",
              duration: 5000,
              offset: 30,
              animationType: "slide-in",
            })
          }
        }}
        callToAction={t('updateTimezone')}             
      />
    )
  } else {
    return null
  }
}, function(_prev, _next) {
  return true
})

const LastUpdatedView = React.memo(function LastUpdatedView() {
  const { t } = useTranslation()
  const productsUpdated = useAtomValue(productsUpdatedAtom)

  if (productsUpdated.lastServerUpdate) {
    return (
      <Text appearance='hint' category='c1' style={{marginHorizontal: 20, textAlign: 'center', marginBottom: 20}} >{t('lastServerUpdate')}: {format(productsUpdated.lastServerUpdate, 'HH:mm dd/MM')}</Text>
    )
  } else {
    return null
  }
}, function(_prev, _next) {
  return true
})

const HomeBottomView = React.memo(function HomeBottomView() {
  if (Platform.OS === 'web') {
    return (
      <>
        <View style={styles.bottomBranding}>
          <View>
            <Text category='s1'>ATLAS</Text>
          </View>
          <Image source={require('../assets/almaty.png')} style={styles.bottomBrandingImage}/>
        </View> 
        <LastUpdatedView />
      </>
    ) 
  } else {
    return (
      <>
        <LastUpdatedView />
        <View style={{height: 50}}></View>
      </>
    )
  }
}, function(_prev, _next) {
  return true
})

// const UpgradePromptView = React.memo(function UpgradeView({ navigation }) {
//   const { t } = useTranslation()
//   const organizationSubscriptionInfo = useAtomValue(organizationSubscriptionInfoAtom)
//   const userRole = useAtomValue(userRoleAtom)
//   if (userRole?.id === 'admin' && Platform.OS !== 'android' && organizationSubscriptionInfo?.onFreePlan && !organizationSubscriptionInfo?.overBillingLimits && !organizationSubscriptionInfo?.failedPayment) {
//     return (
//       <HintView 
//         iconName='award-outline'
//         style={styles.alertHintView}
//         text={t('home.upgradePromptText')}
//         action={() => navigation.navigate('Paywall', {showCloseButton: true})}
//         callToAction={t('home.upgrade')}
//       />
//     )
//   } else {
//     return null
//   }
// }, function(_prev, _next) {
//   return true
// })


const BillingAlertView = React.memo(function BillingAlertView({ navigation }) {
  
  const { t } = useTranslation()
  const organizationSubscriptionInfo = useAtomValue(organizationSubscriptionInfoAtom)
  const userRole = useAtomValue(userRoleAtom)
  return (
    <>
      { userRole?.id === 'admin' && organizationSubscriptionInfo?.overBillingLimits && !organizationSubscriptionInfo?.isBlocked && (
        <HintView 
          iconName='info-outline'
          style={styles.alertHintView}
          text={t(organizationSubscriptionInfo?.paymentLapsed ? 'paymentLapsedAlertText' : 'salesLimitUpgradeText')}
          action={() => navigation.navigate('Billing')}
          callToAction={t('navigate')}
          appearance='danger'
        />
      )}
      {  userRole?.id === 'admin' && organizationSubscriptionInfo?.failedPayment && (
        <HintView 
          iconName='info-outline'
          style={styles.alertHintView}
          text={t('failedPaymentAlertText')}
          action={() => navigation.navigate('Billing')}
          callToAction={t('updatePaymentMethod')}
          appearance='danger'
        />
      )}
      { organizationSubscriptionInfo?.isBlocked && (
        <HintView 
          iconName='info-outline'
          style={styles.alertHintView}
          text={t(organizationSubscriptionInfo?.paymentLapsed ? 'paymentLapsedAlertText' : 'blockedAlertText')}
          action={() => {
            if (userRole?.id === 'admin') {
              navigation.navigate('Billing')
            } else {
              alert(t('permissionDenied'))
            }
          }}
          callToAction={t('home.upgrade')}
          appearance='danger'
        />
      )}
    </>
  )
}, function(_prev, _next) {
}, function(_prev, _next) {
  return true
})

const HomeScreenCard = React.memo(function HomeScreenCard({ item, navigation }) {
  const theme = useTheme()
  const organization = useAtomValue(organizationAtom)
  const userRole = useAtomValue(userRoleAtom)
  const organizationSubscriptionInfo = useAtomValue(organizationSubscriptionInfoAtom)
  const { t } = useTranslation()
  const [alert] = item.alert ? useAtom(item.alert) : [null]

  if (!userRole || !organization) return null

  if ((userRole?.permissions?.[item.permission] || userRole?.id === 'admin') &&
      (item.organizationIds == null || item.organizationIds.includes(organization?.id)) &&
        (item.module == null || organization?.moduleEnabled?.[item.module]) &&
        !organizationSubscriptionInfo?.isBlocked) {
    return (
      <TouchableOpacity
        style={[styles.item, {backgroundColor: theme['background-basic-color-1']}]}
        onPress={() => item.onPress(navigation)}
        testID={item.testID}
        {...(Platform.OS === 'web' && item.testID ? { dataSet: { testid: item.testID } } : {})}
      >
        {(alert != null && (alert > 0 || alert?.data > 0)) && (
          <View style={styles.cardAlert}>
            <Text adjustsFontSizeToFit={true} style={{color: theme['color-basic-100'] }}>{alert?.data ? alert.data : alert}</Text>
          </View>
        )}
        <Icon name={item.icon} width={50} height={50} fill={theme['text-basic-color']} style={styles.itemImage} />
        <Text
          style={styles.itemTitle}
          category='s2'>
          {t(item.title)}
        </Text>
      </TouchableOpacity>
    )
  } else {
    return null
  }
}, function(prev, next) {
  return true
})

const HomeScreenStats = React.memo(function HomeScreenStats() {
  const { t } = useTranslation()
  const dailyStatsSales = useAtomValue(dailyStatsSalesAtom)
  const user = useAtomValue(userAtom)
  const userRole = useAtomValue(userRoleAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)

  // Ensure all showOnTop stores are shown even if no stats exist yet
  const userStoreId = user?.store?.id
  const statsToShow = { ...dailyStatsSales }
  for (const storeId in storesDict) {
    if (!statsToShow[storeId]) {
      // ATL-1098: `showOnTop` is a boolean for some orgs and { admin, cashier } for 2,497 others.
      // The inline ternary this replaces was correct here but lived in a `.js` file the repo's
      // eslint (`--ext .ts,.tsx`) never sees, and its twin in StoreDetailScreen was NOT correct.
      // One checked helper, one meaning.
      if (showsOnTopFor(storesDict[storeId]?.showOnTop, 'admin') || storeId === userStoreId) {
        statsToShow[storeId] = { saleAmount: 0, saleQuantity: 0 }
      }
    }
  }

  return (
    <View style={styles.statContainer}>
      <View style={styles.statRow}>
        <Text style={styles.statCardValue} appearance='hint'>{t('home.salesForToday')}</Text>
        <Text style={styles.statCardValue} appearance='hint'>{t('home.numberOfChecks')}</Text>
        <Text style={styles.statCardValue} appearance='hint'>{t('home.averageCheck')}</Text>
      </View>
      {
        Object.entries(statsToShow).map(([key, value]) => (
          showsOnTopFor(storesDict[key]?.showOnTop, 'admin') &&
          (key === user?.store?.id || (userRole?.permissions?.['viewOtherStores'] || userRole?.id === 'admin')) && (
            <View style={styles.statRow} key={key}>
              <Text style={styles.statCardValue} appearance='hint'>{currencyFormatter.format(value?.saleAmount || 0)}</Text>
              <Text style={styles.statCardValue} appearance='hint'>{(value?.saleQuantity || 0)}</Text>
              <Text style={styles.statCardValue} appearance='hint'>{currencyFormatter.format(value?.saleQuantity ? (value?.saleAmount / value?.saleQuantity)?.toFixed(0) : 0)}</Text>
            </View>
        )))
      }
    </View>
  )
}, function(_prev, _next) {
  return true
})


const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 12,
  },
  title: {
    textAlign: 'center',
    marginBottom: 12,
  },
  statContainer: {
    marginHorizontal: 33,
    marginBottom: 12,
  },
  statRow: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  statCardValue: {
    fontSize: 14,
    textAlign: 'center',
    alignItems: 'center',
    flex: 1,
    flexShrink: 1,
    marginHorizontal: 8,
    maxWidth: 100,
    marginVertical: 2,
  },
  menuContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    flexWrap: 'wrap',
    marginHorizontal: 20,
  },
  item: {
    justifyContent: 'center',
    aspectRatio: 1.0,
    margin: 8,
    // maxWidth: Dimensions.get('window').width / 2 - 24,
    width: Dimensions.get('window').width < 500 ? ((Dimensions.get('window').width - 40) / 2 - 24) : 140,
    height: Dimensions.get('window').width < 500 ? ((Dimensions.get('window').width - 40) / 2 - 24) : 140,
    borderRadius: 5,
  },
  itemImage: {
    marginTop: '25%',
    marginBottom: 5,
    alignSelf: 'center',
  },
  itemTitle: {
    flex: 1,
    textAlign: 'center',
    lineHeight: 15,
    paddingHorizontal: 5,
  },
  cardAlert: {
    width: 24, height: 24, borderRadius: 12, backgroundColor: 'red', position: 'absolute', top: 6, right: 6, flex: 1, alignItems: 'center', justifyContent: 'center'
  },
  leftHeader: {
    height: 24,
    marginHorizontal: 14,
    marginVertical: 6
  },
  alertHintView: {
    marginHorizontal: 33, marginBottom: 12
  },
  bottomBranding: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', alignSelf: 'stretch', marginTop: 'auto', marginHorizontal: 20},
  bottomBrandingImage: {width: 50, height: 50, resizeMode: 'contain', alignSelf: 'flex-end'},
});
